import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { useApi } from "../api/useApi";
import { useAuth } from "../auth/AuthContext";
import GlassLoaderOverlay from "../components/GlassLoader";

/**
 * Landing point for the "Click here to view your evaluation" link sent in
 * the Thank You email after a completed assessment. Skips OTP: validates the
 * token against GET /api/user/view-evaluation, then logs the user in and
 * drops them straight into /sre-score with their results.
 */
export default function ViewEvaluationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { request } = useApi();

  const [status, setStatus] = useState("checking"); // checking | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setMessage("This link is missing its token. Please log in instead.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await request({
          url: "/user/view-evaluation",
          method: "GET",
          params: { token },
        });

        if (cancelled) return;

        login({
          name: data.name,
          email: data.email,
          domain: data.domain,
        });

        navigate(
          `/sre-score?evaluationId=${encodeURIComponent(
            data.evaluationId,
          )}&domain=${encodeURIComponent(data.domain)}&email=${encodeURIComponent(
            data.email,
          )}&name=${encodeURIComponent(data.name)}`,
          { replace: true },
        );
      } catch (err) {
        if (cancelled) return;

        const reason = err?.reason;
        if (reason === "EXPIRED") {
          setMessage("This link has expired. Please log in to view your evaluation.");
        } else {
          setMessage("This link isn't valid. Please log in instead.");
        }
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "checking") {
    return <GlassLoaderOverlay open />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2,
        textAlign: "center",
        px: 3,
      }}
    >
      <Typography variant="h6">{message}</Typography>
      <Button variant="contained" onClick={() => navigate("/login")}>
        Go to login
      </Button>
    </Box>
  );
}